import { useRef } from 'react'
import { useMount } from '~/shared/hooks/useMount'
import { TIMEOUT_NOTIFICATION_KEY } from '~/shared/notifications/Notifications'
import type { FC } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useIdleTimer } from 'react-idle-timer'
import { useAuth0 } from '@auth0/auth0-react'
import { useLogoutEffects } from '~/app/auth'
import {
  getSessionToken,
  setIdToken,
  setSessionToken,
  tokenExpiresAt,
  requireAccessToken,
} from '~/shared/lib/auth'
import { useActiveVideoVisit } from '~/shared/hooks/activeVideoVisit'
import { queueNotification } from '~/shared/notifications/actions'
import { jwtDecode } from 'jwt-decode'
import { logger } from '~/shared/lib/logger'
import { fireAndForget } from '~/shared/lib/fireAndForget'

const TOKEN_TTL = 10790000

const secondsUntil = (epochSeconds: number) => epochSeconds - Math.floor(Date.now() / 1000)

/**
 * Listens to all on-screen activity, including mouse and keyboard events.
 * Events will trigger a token refresh and reset the timer, but when it expires,
 * the logged in user will be logged out and their token removed from storage.
 * They will be shown a "session expired" message that will persist until they
 * log in again.
 */
const SessionNotifier: FC = () => {
  const timeoutIdRef = useRef<NodeJS.Timeout | undefined>(undefined)
  const auth0 = useAuth0()
  const dispatch = useDispatch()
  const { handler: handleLogout } = useLogoutEffects({ auth0: { openUrl: false } })
  const currentlyOnVideo = !!useActiveVideoVisit().activeVideoVisitId
  const userId = useSelector(state => state.me.id)

  const onIdleTimeout = async () => {
    logger.debug('[Session] onIdleTimeout for User', userId, 'currentlyOnVideo', currentlyOnVideo)

    // If there is an active visit, keep the session alive so that we don't force a logout for a clinician
    if (currentlyOnVideo) {
      await refreshToken()
      return
    }

    forceLogout()
  }

  const forceLogout = () => {
    dispatch(
      queueNotification({
        message: 'Your session has expired',
        variant: 'info',
        persist: true,
        key: TIMEOUT_NOTIFICATION_KEY,
      }),
    )
    void handleLogout()
  }

  // verify token valid, if invalid force logout
  const onUserAction = async (opts?: { isRetry?: boolean }) => {
    const token = getSessionToken()
    const decodedToken = token ? jwtDecode(token) : null

    /**
     * This block should usually not execute because our timer should be refreshing token with 1 min buffer
     */
    if (decodedToken?.exp != null && secondsUntil(decodedToken.exp) < -10) {
      logger.debug('[Session] decodedToken.exp for User', userId, decodedToken)
      if (!opts?.isRetry) {
        // The token is expired, but before forcing a logout, try to refresh the token
        // then, re-try once more to see if we should still be logged out (i.e. if the refresh token was also expired)
        logger.debug('[Session] refreshing token in onUserAction')
        await refreshToken()
        return onUserAction({ isRetry: true })
      } else {
        logger.debug('[Session] skipping attempt to refresh token in onUserAction', {
          isRetry: opts?.isRetry,
        })
      }
      forceLogout()
    }
  }

  /**
   * Refreshes the token every 1 minute before it is set to expire
   */
  const refreshToken = async () => {
    if (timeoutIdRef.current) clearTimeout(timeoutIdRef.current)
    const token = getSessionToken()
    if (token) {
      try {
        // Refresh token if it's about to expire
        let remainingTimeInSeconds = secondsUntil(tokenExpiresAt(token))
        if (remainingTimeInSeconds <= 60) {
          const newToken = requireAccessToken(
            await auth0.getAccessTokenSilently({
              cacheMode: 'off',
            }),
          )
          const idTokenClaims = await auth0.getIdTokenClaims()
          const idToken = idTokenClaims?.__raw
          setSessionToken(newToken)
          if (idToken) {
            setIdToken(idToken)
          }
          remainingTimeInSeconds = secondsUntil(tokenExpiresAt(newToken))
        }

        const timeBeforeNextRefreshInMilliseconds = (remainingTimeInSeconds - 60) * 1000
        timeoutIdRef.current = setTimeout(
          () => void refreshToken(),
          timeBeforeNextRefreshInMilliseconds,
        )
      } catch (e) {
        logger.error('[Session] refreshToken failure for User', userId, e)
        forceLogout()
      }
    }
  }

  // Start token refresh loop
  useMount(() => {
    void refreshToken()
  })

  useIdleTimer({
    timeout: TOKEN_TTL,
    // onIdleTimeout only reaches refreshToken and forceLogout, both of which absorb their own
    // failures; onUserAction can still reject, since jwtDecode throws on a malformed token.
    onIdle: () => void onIdleTimeout(),
    onAction: () => fireAndForget('[Session] onUserAction', onUserAction),
    throttle: 10000,
  })

  return null
}

export default SessionNotifier
